import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useColorMode } from "../../theme/ThemeProviderWrapper";
import { Env_Factor_Options } from "../../db_access/env_factor_options";
import { Emission_Detail_Options } from "../../db_access/emission_detail_options";
import "../../style/components-style/MaterialSearchCellEditor.css";

const MaterialSearchInput = ({ onSelect, searchType = "env_factor" }) => {
    const theme = useTheme();
    const { mode } = useColorMode();
    const isDark = mode === "dark";
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const containerRef = useRef(null);
    const isSelecting = useRef(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setSearchResults([]);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const search = async () => {
            if (searchTerm.length < 2) {
                setSearchResults([]);
                return;
            }

            setIsLoading(true);
            try {
                const response = searchType === "emission_detail"
                    ? await Emission_Detail_Options.searchMaterials(searchTerm)
                    : await Env_Factor_Options.searchMaterials(searchTerm);
                const data = await response.json();
                setSearchResults(data || []);
                setSelectedIndex(0);
            } catch (error) {
                console.error("Error searching materials:", error);
                setSearchResults([]);
            } finally {
                setIsLoading(false);
            }
        };

        const debounceTimer = setTimeout(search, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, searchType]);

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex((prev) => Math.min(prev + 1, searchResults.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter") {
            if (searchResults.length > 0) {
                e.preventDefault();
                selectMaterial(searchResults[selectedIndex]);
            }
        } else if (e.key === "Escape") {
            setSearchResults([]);
        }
    };

    const selectMaterial = (material) => {
        if (isSelecting.current) return; // Prevent double addition
        isSelecting.current = true;
        
        if (onSelect) {
            onSelect(material);
        }
        setSearchTerm("");
        setSearchResults([]);
        
        // Release lock after a short delay to allow React state to settle
        setTimeout(() => {
            isSelecting.current = false;
        }, 300);
    };

    return (
        <div className="material-search-editor" ref={containerRef} style={{ position: "relative", width: "400px", zIndex: 1000, margin: "auto" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <input
                    ref={inputRef}
                    type="text"
                    className="material-search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search"
                    style={{
                        height: "36px",
                        padding: "8px 60px 8px 8px",
                        borderRadius: "4px",
                        border: isDark ? "1px solid #3f3f46" : "1px solid #ccc",
                        width: "100%",
                        fontSize: "14px",
                        backgroundColor: isDark ? "#27272a" : "#fff",
                        color: isDark ? "#f1f5f9" : "#000",
                        outline: "none",
                    }}
                />
                {isLoading && (
                    <div
                        style={{
                            position: "absolute",
                            right: searchTerm ? "35px" : "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "12px",
                            color: isDark ? "#a1a1aa" : "#999",
                        }}
                    >
                        ⏳
                    </div>
                )}
                {searchTerm && !isLoading && (
                    <button
                        onClick={() => {
                            setSearchTerm("");
                            setSearchResults([]);
                            inputRef.current?.focus();
                        }}
                        style={{
                            position: "absolute",
                            right: "8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "16px",
                            color: isDark ? "#a1a1aa" : "#999",
                            padding: "4px 8px",
                            lineHeight: 1,
                        }}
                        title="Clear search"
                    >
                        ✕
                    </button>
                )}
            </div>

            {searchResults.length > 0 && (
                <div
                    className="material-search-results"
                    ref={listRef}
                    style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        left: "auto",
                        minWidth: "850px",
                        maxHeight: "220px",
                        overflowY: "auto",
                        background: isDark ? "#1c1c1e" : "white",
                        border: isDark ? "1px solid #3f3f46" : "1px solid #ddd",
                        boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.5)" : "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 1001,
                        borderRadius: "0 0 4px 4px",
                    }}
                >
                    {searchResults.map((material, index) => {
                        const parts = [];
                        if (material.length !== null && material.length !== undefined && material.length !== "") parts.push(`${material.length}${material.length_unit || ""}`);
                        if (material.width !== null && material.width !== undefined && material.width !== "") parts.push(`${material.width}${material.width_unit || ""}`);
                        if (material.thickness !== null && material.thickness !== undefined && material.thickness !== "") parts.push(`${material.thickness}${material.thickness_unit || ""}`);
                        const dimString = parts.length > 0 ? parts.join(" × ") : null;

                        return (
                            <div
                                key={index}
                                className={`material-search-item ${index === selectedIndex ? "selected" : ""}`}
                                onClick={() => selectMaterial(material)}
                                onMouseEnter={() => setSelectedIndex(index)}
                                style={{
                                    background: index === selectedIndex ? (isDark ? "#27272a" : "#f0f0f0") : "transparent",
                                }}
                            >
                                <div className="material-item-left">
                                    <div className="material-description" style={{ fontWeight: "600", fontSize: "16px", marginBottom: "4px", color: isDark ? "#f8fafc" : "#000" }}>
                                        {material.description}
                                    </div>
                                    <div className="material-details" style={{ fontSize: "14px", color: isDark ? "#94a3b8" : "#666" }}>
                                        <span className="material-type" style={{ color: isDark ? "#60a5fa" : "#1976d2" }}>{material.type}</span>
                                        {" → "}
                                        <span className="material-category" style={{ color: isDark ? "#4ade80" : "#388e3c" }}>{material.category}</span>
                                        {" → "}
                                        <span className="material-subcategory" style={{ color: isDark ? "#fb923c" : "#f57c00" }}>{material.sub_category}</span>
                                    </div>
                                </div>
                                <div className="material-item-right">
                                    {dimString && (
                                        <span className="material-dim-pill">
                                            📏 {dimString}
                                        </span>
                                    )}
                                    {material.unit && (
                                        <span className="material-unit-pill">
                                            Unit: {material.unit}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {!isLoading && searchTerm.length >= 2 && searchResults.length === 0 && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        left: "auto",
                        minWidth: "850px",
                        background: isDark ? "#1c1c1e" : "white",
                        border: isDark ? "1px solid #3f3f46" : "1px solid #ddd",
                        boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.5)" : "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 1001,
                        borderRadius: "0 0 4px 4px",
                        padding: "20px",
                        textAlign: "center",
                        color: isDark ? "#94a3b8" : "#999",
                        fontSize: "14px",
                    }}
                >
                    No materials found for "{searchTerm}"
                </div>
            )}
        </div>
    );
};

export default MaterialSearchInput;
